import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ThermalBillPrint from '../components/ThermalBillPrint';
import A4InvoicePDF from '../components/A4InvoicePDF';
import { Package, AlertTriangle, Calendar, X, Check, Hash, Camera, Grid3x3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pos`;

// Batch Picker Modal Component
const BatchPickerModal = ({ product, batches, customerLevel, onSelect, onClose }) => {
  const today = new Date().toISOString().split('T')[0];
  
  // Get price for batch based on customer level
  const getBatchPrice = (batch) => {
    const level = customerLevel || 1;
    if (level === 5 && batch.selling_price_l5) return batch.selling_price_l5;
    if (level === 4 && batch.selling_price_l4) return batch.selling_price_l4;
    if (level === 3 && batch.selling_price_l3) return batch.selling_price_l3;
    if (level === 2 && batch.selling_price_l2) return batch.selling_price_l2;
    return batch.selling_price || 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return expiryDate < today;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No Expiry';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Select Batch</h3>
              <p className="text-sm text-slate-500">{product.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
            <Calendar size={14} />
            Batches sorted by expiry date (First Expiry First Out)
          </p>
          
          <div className="space-y-2">
            {batches.map((batch) => {
              const expired = isExpired(batch.expiry_date);
              const price = getBatchPrice(batch);
              
              return (
                <div
                  key={batch.id}
                  onClick={() => !expired && onSelect(batch, price)}
                  className={`p-4 border rounded-xl transition-all ${
                    expired 
                      ? 'bg-red-50 border-red-200 cursor-not-allowed opacity-60' 
                      : 'bg-white border-slate-200 hover:border-blue-400 hover:shadow-md cursor-pointer'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{batch.batch_number}</span>
                        {expired && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full flex items-center gap-1">
                            <AlertTriangle size={12} />
                            EXPIRED
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span>Stock: <strong className="text-slate-700">{batch.quantity}</strong></span>
                        <span>Expiry: <strong className={expired ? 'text-red-600' : 'text-slate-700'}>{formatDate(batch.expiry_date)}</strong></span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">₹{price.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">Level {customerLevel || 1} Price</p>
                    </div>
                  </div>
                  {expired && (
                    <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      Cannot sell expired products
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          
          {batches.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <Package size={48} className="mx-auto mb-2 opacity-50" />
              <p>No active batches available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const POSBilling = () => {
  // Edit mode (when redirected from BillsHistory with ?edit=<bill_id>)
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editBillId = searchParams.get('edit');
  const editType = searchParams.get('type'); // 'estimation' | null
  const [isEditMode, setIsEditMode] = useState(!!editBillId);

  // State
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [cart, setCart] = useState([]);
  const [heldBills, setHeldBills] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHeldBillsModal, setShowHeldBillsModal] = useState(false);
  const [billDiscount, setBillDiscount] = useState({ type: 'percent', value: 0 });
  const [newCustomer, setNewCustomer] = useState({ name: '', mobile: '', email: '', gst_number: '' });
  const [paymentModes, setPaymentModes] = useState([{ mode: 'cash', amount: 0 }]);
  const [loading, setLoading] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const customerSearchRef = useRef(null);
  
  // Cash Register State
  const [registerStatus, setRegisterStatus] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(true);

  // Estimation Mode
  const [isEstimation, setIsEstimation] = useState(false);
  // Grand Total Round-off (toggle in Bill Summary; default from bill settings)
  const [roundOffEnabled, setRoundOffEnabled] = useState(false);
  
  // Print Preview
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printBillData, setPrintBillData] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [billSettings, setBillSettings] = useState(null);
  const [billNotes, setBillNotes] = useState('');
  
  // Customer Custom Prices
  const [customerCustomPrices, setCustomerCustomPrices] = useState({});

  // Batch Picker State
  const [showBatchPicker, setShowBatchPicker] = useState(false);
  const [batchPickerProduct, setBatchPickerProduct] = useState(null);
  const [productBatches, setProductBatches] = useState([]);
  
  // Serial Number Picker State
  const [showSerialPicker, setShowSerialPicker] = useState(false);
  const [serialPickerProduct, setSerialPickerProduct] = useState(null);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [serialSearchText, setSerialSearchText] = useState('');
  
  // User Type for permission checks
  const [userType, setUserType] = useState('owner');
  
  // Quick Add Custom Item
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({ name: '', description: '', price: '', qty: 1, tax_percent: 0 });
  
  // Barcode Scanner
  const [showScanner, setShowScanner] = useState(false);
  const scannerRef = useRef(null);

  // Primary Products Drawer (cafe-style quick pick)
  const [showPrimaryDrawer, setShowPrimaryDrawer] = useState(false);
  const [primaryProducts, setPrimaryProducts] = useState([]);
  const [primaryCategoryFilter, setPrimaryCategoryFilter] = useState('');
  const [billingFeatures, setBillingFeatures] = useState({ product_images_in_billing: false });

  // Qty Entry Popup (toggled via Bill Settings → enable_qty_popup)
  const [showQtyPopup, setShowQtyPopup] = useState(false);
  const [qtyPopupProduct, setQtyPopupProduct] = useState(null);
  const [qtyPopupForm, setQtyPopupForm] = useState({ qty: '1', price: '', discount: '0', pcs: '' });

  // Fetch primary products & feature flags once when settings indicate drawer is enabled
  useEffect(() => {
    if (!billSettings?.show_primary_drawer) return;
    const token = localStorage.getItem('pos_token');
    axios.get(`${API}/products/primary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPrimaryProducts(res.data || []))
      .catch(err => console.error('Failed to load primary products:', err));
    axios.get(`${API}/features/billing`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setBillingFeatures(res.data || { product_images_in_billing: false }))
      .catch(() => {});
  }, [billSettings?.show_primary_drawer]);

  // Load bill for editing if ?edit=<id> query param is set
  useEffect(() => {
    if (!editBillId) {
      // Exiting edit mode → reset state
      setIsEditMode(false);
      return;
    }
    const loadBillForEdit = async () => {
      // Reset state so prior edit's data does not leak into this edit
      setCart([]);
      setSelectedCustomer(null);
      setPaymentModes([{ mode: 'cash', amount: 0 }]);
      setBillNotes('');
      try {
        const token = localStorage.getItem('pos_token');
        const endpoint = editType === 'estimation' ? `${API}/estimations/${editBillId}` : `${API}/bills/${editBillId}`;
        const res = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        const bill = res.data;
        if (!bill) return;
        // Hydrate cart from bill items
        setCart((bill.items || []).map(it => ({
          product_id: it.product_id,
          product_name: it.product_name,
          sku: it.sku || '',
          description: it.description || '',
          quantity: it.quantity || 0,
          price: it.price || 0,
          price_type: it.price_type || 'default',
          mrp: it.mrp || 0,
          hsn_code: it.hsn_code || '',
          tax_percent: it.tax_percent || 0,
          tax_amount: it.tax_amount || 0,
          item_discount: it.item_discount || 0,
          total: it.total || 0,
          max_stock: 99999,
          stock_quantity: it.stock_quantity ?? null,
          stock_unit: it.stock_unit || null,
          track_stock_separately: it.stock_quantity != null,
          batch_id: it.batch_id,
          serial_numbers: it.serial_numbers || [],
          serial_number_values: it.serial_number_values || [],
          is_custom_item: !!it.is_custom_item,
        })));
        // Hydrate customer — fetch full record so balance/price_level/etc are present.
        if (bill.customer_id && bill.customer_id !== 'guest') {
          try {
            const cRes = await axios.get(`${API}/customers/${bill.customer_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (cRes.data) {
              setSelectedCustomer(cRes.data);
              fetchCustomerCustomPrices(bill.customer_id);
            } else {
              throw new Error('empty');
            }
          } catch (cErr) {
            // Fallback: construct minimal customer from bill fields
            setSelectedCustomer({
              id: bill.customer_id,
              name: bill.customer_name || '',
              mobile: bill.customer_mobile || '',
              gst_number: bill.customer_gst_number || '',
              email: bill.customer_email || '',
              address: bill.customer_address || ''
            });
          }
        } else {
          // Guest bill in edit mode — explicitly mark as guest so UI doesn't flash empty
          setSelectedCustomer({ id: 'guest', name: 'Guest', mobile: '', is_guest: true });
        }
        setPaymentModes((bill.payment_modes && bill.payment_modes.length) ? bill.payment_modes : [{ mode: 'cash', amount: bill.grand_total || 0 }]);
        setBillNotes(bill.notes || '');
        setIsEditMode(true);
      } catch (err) {
        console.error('Failed to load bill for edit:', err);
        alert('Could not load bill for editing.');
      }
    };
    loadBillForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBillId, editType]);
  
  // Initialize camera barcode scanner
  useEffect(() => {
    if (!showScanner) return;
    let html5QrCode = null;
    const initScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        html5QrCode = new Html5Qrcode("barcode-scanner-region");
        scannerRef.current = html5QrCode;
        await html5QrCode.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 100 }, aspectRatio: 1.0 },
          (decodedText) => {
            // Barcode scanned - find product and add to cart
            const matchedProduct = products.find(p =>
              (p.barcode && p.barcode === decodedText) || (p.sku && p.sku === decodedText)
            );
            if (matchedProduct) {
              addToCart(matchedProduct);
            } else {
              setProductSearch(decodedText);
              setShowProductSearch(true);
            }
            // Stop scanner after successful scan
            try { html5QrCode.stop(); } catch(e) {}
            scannerRef.current = null;
            setShowScanner(false);
          }
        );
      } catch (err) {
        console.error('Scanner init failed:', err);
      }
    };
    // Delay to allow DOM render
    setTimeout(initScanner, 300);
    return () => {
      if (html5QrCode) {
        try { html5QrCode.stop(); } catch(e) {}
      }
    };
  }, [showScanner]);
  
  // Previous date billing
  const [selectedBillDate, setSelectedBillDate] = useState('');
  // Fetch cash register status
  const fetchRegisterStatus = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/cash-register/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegisterStatus(response.data);
    } catch (error) {
      console.error('Error fetching register status:', error);
      setRegisterStatus({ status: 'closed' });
    } finally {
      setRegisterLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchRegisterStatus();
    fetchCustomers();
    fetchProducts();
    fetchTaxGroups();
    fetchHeldBills();
    // Skip guest preselect when entering edit mode — the bill's customer will be hydrated by the edit useEffect
    if (!editBillId) {
      selectGuestCustomer();
    }
    fetchBusinessInfo();
    fetchBillSettings();
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserType(response.data.type || response.data.user_type || 'owner');
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchBusinessInfo = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(response.data);
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const fetchBillSettings = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/bill`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillSettings(response.data);
      // Initialise round-off toggle default from saved settings
      setRoundOffEnabled(response.data?.enable_round_off === true);
      // Set default payment mode from settings
      if (response.data?.default_bill_mode) {
        setPaymentModes([{ mode: response.data.default_bill_mode, amount: 0 }]);
      }
    } catch (error) {
      console.error('Error fetching bill settings:', error);
    }
  };

  // Focus search on shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'F3') {
        e.preventDefault();
        handleCreateBill();
      }
      if (e.key === 'F4') {
        e.preventDefault();
        customerSearchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowProductSearch(false);
        setProductSearch('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out products marked as "exception_for_sale" (raw materials)
      const saleProducts = response.data.filter(p => !p.exception_for_sale);
      setProducts(saleProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTaxGroups = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/tax-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxGroups(response.data);
    } catch (error) {
      console.error('Error fetching tax groups:', error);
    }
  };

  const fetchHeldBills = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/bills?status=held`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHeldBills(response.data);
    } catch (error) {
      console.error('Error fetching held bills:', error);
    }
  };

  // Fetch custom prices for a customer
  const fetchCustomerCustomPrices = async (customerId) => {
    if (!customerId || customerId === 'guest') {
      setCustomerCustomPrices({});
      return;
    }
    
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers/${customerId}/custom-prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert to a map for quick lookup
      const priceMap = {};
      response.data.forEach(cp => {
        priceMap[cp.product_id] = cp.custom_price;
      });
      setCustomerCustomPrices(priceMap);
    } catch (error) {
      console.error('Error fetching customer custom prices:', error);
      setCustomerCustomPrices({});
    }
  };

  const selectGuestCustomer = useCallback(() => {
    const guest = customers.find(c => c.is_guest) || { 
      id: 'guest', 
      name: 'Guest', 
      mobile: '', 
      is_guest: true 
    };
    setSelectedCustomer(guest);
    setCustomerCustomPrices({});
  }, [customers]);

  const addNewCustomer = async () => {
    if (!newCustomer.name || !newCustomer.mobile) {
      alert('Please enter name and mobile number');
      return;
    }

    try {
      const token = localStorage.getItem('pos_token');
      // Only send non-empty values
      const customerData = {
        name: newCustomer.name,
        mobile: newCustomer.mobile,
        email: newCustomer.email || null,
        gst_number: newCustomer.gst_number || null
      };
      const response = await axios.post(`${API}/customers`, customerData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCustomer(response.data);
      fetchCustomerCustomPrices(response.data.id);
      setShowCustomerModal(false);
      setNewCustomer({ name: '', mobile: '', email: '', gst_number: '' });
      fetchCustomers();
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to add customer'));
    }
  };

  // Get default tax for a product
  const getProductTax = (product) => {
    if (product.tax_group_id && taxGroups.length > 0) {
      const taxGroup = taxGroups.find(t => t.id === product.tax_group_id);
      if (taxGroup && !taxGroup.is_nil) {
        return taxGroup.cgst + taxGroup.sgst;
      }
    }
    return 0;
  };

  // Calculate tax based on tax_method setting (inclusive or exclusive)
  // When Tax Inclusive: Price already includes tax, so we reverse calculate
  // When Tax Exclusive: Tax is added on top of price
  const calculateTaxForItem = (price, taxPercent, quantity = 1, itemDiscount = 0) => {
    const baseAmount = (price * quantity) - itemDiscount;
    const taxMethod = billSettings?.tax_method || 'inclusive';
    
    if (taxMethod === 'inclusive') {
      // Tax is already included in the price
      // Base Price = Selling Price / (1 + Tax%)
      // Tax Amount = Selling Price - Base Price
      const taxDivisor = 1 + (taxPercent / 100);
      const preTaxAmount = baseAmount / taxDivisor;
      const taxAmount = baseAmount - preTaxAmount;
      return {
        taxAmount: taxAmount,
        total: baseAmount, // Total remains same as price already includes tax
        preTaxAmount: preTaxAmount
      };
    } else {
      // Tax Exclusive - add tax on top
      const taxAmount = (baseAmount * taxPercent) / 100;
      return {
        taxAmount: taxAmount,
        total: baseAmount + taxAmount,
        preTaxAmount: baseAmount
      };
    }
  };

  // Get product price based on customer custom price, then pricing level
  const getProductPrice = (product) => {
    // 1. Check customer-specific custom price first
    if (selectedCustomer && selectedCustomer.id !== 'guest') {
      const customPrice = customerCustomPrices[product.id];
      if (customPrice !== undefined) {
        return { price: customPrice, type: 'custom' };
      }
    }
    
    // 2. Check level-based pricing
    const level = selectedCustomer?.pricing_level || 1;
    
    if (level === 5 && product.selling_price_l5) return { price: product.selling_price_l5, type: `level_${level}` };
    if (level === 4 && product.selling_price_l4) return { price: product.selling_price_l4, type: `level_${level}` };
    if (level === 3 && product.selling_price_l3) return { price: product.selling_price_l3, type: `level_${level}` };
    if (level === 2 && product.selling_price_l2) return { price: product.selling_price_l2, type: `level_${level}` };
    
    // 3. Default to Level 1 (selling_price)
    return { price: product.selling_price, type: 'default' };
  };

  // Build consolidated "note" from product master based on its print-in-bill flags
  // Pipe-separated, values only. Re-generated on every addToCart.
  const buildProductNote = (product) => {
    const parts = [];
    if (product.print_size_in_bill && product.size) parts.push(product.size);
    if (product.print_color_in_bill && product.color) parts.push(product.color);
    if (product.print_description_in_bill && product.description) parts.push(product.description);
    return parts.join(' | ');
  };

  // Confirm quantity popup → add to cart with chosen qty/price/discount
  const confirmQtyPopup = () => {
    if (!qtyPopupProduct) return;
    const qty = parseFloat(qtyPopupForm.qty);
    const price = parseFloat(qtyPopupForm.price);
    const discount = parseFloat(qtyPopupForm.discount) || 0;
    const isDual = billSettings?.enable_dual_unit_stock && qtyPopupProduct.track_stock_separately;
    const pcs = isDual ? parseFloat(qtyPopupForm.pcs) : null;
    if (!qty || qty <= 0) {
      alert('Please enter a valid quantity');
      return;
    }
    if (price < 0) {
      alert('Price cannot be negative');
      return;
    }
    if (isDual && (!pcs || pcs <= 0)) {
      alert(`Please enter valid pieces (${qtyPopupProduct.stock_unit || 'pcs'})`);
      return;
    }
    const skipStockValidation = businessInfo?.inventory_model === 'no_inventory';
    // Dual-unit: stock is held in pcs, so validate the pcs entered (not the sale qty)
    const stockCheckQty = isDual ? pcs : qty;
    if (!skipStockValidation && stockCheckQty > (qtyPopupProduct.current_stock || 0)) {
      alert(`Only ${qtyPopupProduct.current_stock} ${isDual ? (qtyPopupProduct.stock_unit || 'pcs') : ''} in stock`);
      return;
    }
    const taxPercent = getProductTax(qtyPopupProduct);
    const taxCalc = calculateTaxForItem(price, taxPercent, qty, discount);
    setCart([...cart, {
      product_id: qtyPopupProduct.id,
      product_name: qtyPopupProduct.name,
      sku: qtyPopupProduct.sku,
      description: buildProductNote(qtyPopupProduct),
      quantity: qty,
      price: price,
      price_type: price !== (qtyPopupProduct.selling_price || 0) ? 'manual' : 'default',
      mrp: qtyPopupProduct.mrp,
      hsn_code: qtyPopupProduct.hsn_code || '',
      unit: qtyPopupProduct.unit || null,
      tax_percent: taxPercent,
      tax_amount: taxCalc.taxAmount,
      item_discount: discount,
      total: taxCalc.total,
      stock_quantity: isDual ? pcs : null,
      stock_unit: isDual ? (qtyPopupProduct.stock_unit || 'pcs') : null,
      track_stock_separately: !!isDual,
      max_stock: skipStockValidation ? 99999 : qtyPopupProduct.current_stock,
      loose_sales_type: qtyPopupProduct.loose_sales_type || null,
      loose_sales_allowed: qtyPopupProduct.loose_sales_allowed || false
    }]);
    setShowQtyPopup(false);
    setQtyPopupProduct(null);
    // Auto-trigger F2 behaviour: clear search & refocus SKU/Product input
    // so the cashier can immediately scan/type the next item (continuous workflow).
    setProductSearch('');
    setShowProductSearch(false);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const addToCart = async (product) => {
    // Skip stock validation if inventory model is no_inventory
    const skipStockValidation = businessInfo?.inventory_model === 'no_inventory';
    
    if (!skipStockValidation && product.current_stock <= 0) {
      alert('Product is out of stock!');
      return;
    }

    // If Qty Entry Popup is enabled and product doesn't need special pickers (serial/batch),
    // open popup so cashier can enter qty/price/discount in one shot.
    if (
      (billSettings?.enable_qty_popup || (billSettings?.enable_dual_unit_stock && product.track_stock_separately)) &&
      !product.has_serial_number &&
      !product.track_batch_expiry &&
      !product.loose_sales_allowed
    ) {
      const { price } = getProductPrice(product);
      const isDual = billSettings?.enable_dual_unit_stock && product.track_stock_separately;
      setQtyPopupProduct(product);
      setQtyPopupForm({ qty: '1', price: String(price ?? product.selling_price ?? 0), discount: '0', pcs: isDual ? '1' : '' });
      setShowQtyPopup(true);
      return;
    }

    // Check if product has serial number tracking
    if (product.has_serial_number) {
      try {
        const token = localStorage.getItem('pos_token');
        const resp = await axios.get(`${API}/serial-numbers/${product.id}?status=available`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableSerials(resp.data || []);
        setSerialPickerProduct(product);
        setSelectedSerials([]);
        setSerialSearchText('');
        setShowSerialPicker(true);
        return;
      } catch (err) {
        console.error('Error fetching serials:', err);
      }
    }

    // Check if product has batch tracking enabled
    if (product.track_batch_expiry) {
      try {
        const token = localStorage.getItem('pos_token');
        // Include expired batches to show them with EXPIRED badge (but not selectable)
        const response = await axios.get(`${API}/product-batches/${product.id}?include_expired=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const activeBatches = response.data.filter(b => b.quantity > 0);
        
        if (activeBatches.length > 1) {
          // Multiple batches - show picker
          setBatchPickerProduct(product);
          setProductBatches(activeBatches);
          setShowBatchPicker(true);
          return;
        } else if (activeBatches.length === 1) {
          // Single batch - use it directly but check expiry
          const batch = activeBatches[0];
          const today = new Date().toISOString().split('T')[0];
          
          if (batch.expiry_date && batch.expiry_date < today) {
            alert('Cannot sell expired products! This batch has expired.');
            return;
          }
          
          // Add to cart with batch info
          addToCartWithBatch(product, batch);
          return;
        }
        // No batches found - fall through to regular add
      } catch (error) {
        console.error('Error fetching batches:', error);
        // Fall through to regular add if error
      }
    }

    // Regular add to cart (no batch tracking)
    addToCartRegular(product);
  };

  // Add product to cart with batch info
  const addToCartWithBatch = (product, batch, overridePrice = null) => {
    const customerLevel = selectedCustomer?.pricing_level || 1;
    
    // Get price from batch based on customer level
    let price = overridePrice;
    if (!price) {
      if (customerLevel === 5 && batch.selling_price_l5) price = batch.selling_price_l5;
      else if (customerLevel === 4 && batch.selling_price_l4) price = batch.selling_price_l4;
      else if (customerLevel === 3 && batch.selling_price_l3) price = batch.selling_price_l3;
      else if (customerLevel === 2 && batch.selling_price_l2) price = batch.selling_price_l2;
      else price = batch.selling_price || product.selling_price;
    }

    const taxPercent = getProductTax(product);
    const existingItem = cart.find(item => item.product_id === product.id && item.batch_id === batch.id);
    
    if (existingItem) {
      if (existingItem.quantity >= batch.quantity) {
        alert('Not enough stock in this batch!');
        return;
      }
      updateQuantity(product.id, existingItem.quantity + 1, batch.id);
    } else {
      const taxCalc = calculateTaxForItem(price, taxPercent, 1, 0);
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        description: buildProductNote(product),
        quantity: 1,
        price: price,
        price_type: `batch_level_${customerLevel}`,
        mrp: batch.mrp || product.mrp,
        hsn_code: product.hsn_code || '',
        tax_percent: taxPercent,
        tax_amount: taxCalc.taxAmount,
        item_discount: 0,
        total: taxCalc.total,
        max_stock: batch.quantity,
        // Batch info
        batch_id: batch.id,
        batch_number: batch.batch_number,
        expiry_date: batch.expiry_date
      }]);
    }
    
    setProductSearch('');
    setShowProductSearch(false);
    setShowBatchPicker(false);
    setBatchPickerProduct(null);
  };

  // Handle batch selection from modal
  const handleBatchSelect = (batch, price) => {
    if (batchPickerProduct) {
      addToCartWithBatch(batchPickerProduct, batch, price);
    }
  };

  // Add product to cart with serial numbers
  const addToCartWithSerials = (product, serials) => {
    const taxPercent = getProductTax(product);
    const priceInfo = getProductPrice(product);
    const price = priceInfo.price;
    const priceType = priceInfo.type;
    const qty = serials.length;
    const taxCalc = calculateTaxForItem(price, taxPercent, qty, 0);
    
    setCart(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      description: buildProductNote(product),
      quantity: qty,
      price: price,
      price_type: priceType,
      mrp: product.mrp,
      hsn_code: product.hsn_code || '',
      tax_percent: taxPercent,
      tax_amount: taxCalc.taxAmount,
      item_discount: 0,
      total: taxCalc.total,
      max_stock: product.current_stock,
      serial_numbers: serials.map(s => s.id),
      serial_number_values: serials.map(s => s.serial_number),
      has_serial: true
    }]);
    setShowSerialPicker(false);
    setSerialPickerProduct(null);
  };

  // Regular add to cart (for products without batch tracking)
  const addToCartRegular = (product) => {
    const skipStockValidation = businessInfo?.inventory_model === 'no_inventory';
    
    const taxPercent = getProductTax(product);
    const existingItem = cart.find(item => item.product_id === product.id && !item.batch_id);
    
    // Determine increment based on loose sales configuration
    const isLooseSale = product.loose_sales_type || product.loose_sales_allowed;
    const increment = isLooseSale ? 0.5 : 1;
    
    if (existingItem) {
      if (!skipStockValidation && existingItem.quantity >= product.current_stock) {
        alert('Not enough stock available!');
        return;
      }
      updateQuantity(product.id, existingItem.quantity + increment);
    } else {
      // Get price based on customer custom price or pricing level
      const priceInfo = getProductPrice(product);
      const price = priceInfo.price;
      const priceType = priceInfo.type;
      const taxCalc = calculateTaxForItem(price, taxPercent, 1, 0);
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        description: buildProductNote(product),
        quantity: 1,
        price: price,
        price_type: priceType,
        mrp: product.mrp,
        hsn_code: product.hsn_code || '',
        tax_percent: taxPercent,
        tax_amount: taxCalc.taxAmount,
        item_discount: 0,
        total: taxCalc.total,
        max_stock: skipStockValidation ? 99999 : product.current_stock,
        loose_sales_type: product.loose_sales_type || null,
        loose_sales_allowed: product.loose_sales_allowed || false
      }]);
    }
    setProductSearch('');
    setShowProductSearch(false);
  };

  // Add custom non-stock item to cart
  const addCustomItemToCart = () => {
    if (!quickAddForm.name || !quickAddForm.price) return;
    const price = parseFloat(quickAddForm.price);
    const qty = parseFloat(quickAddForm.qty) || 1;
    const taxPercent = parseFloat(quickAddForm.tax_percent) || 0;
    const taxCalc = calculateTaxForItem(price, taxPercent, qty, 0);
    
    setCart([...cart, {
      product_id: `custom_${Date.now()}`,
      product_name: quickAddForm.name,
      sku: '',
      description: quickAddForm.description || '',
      quantity: qty,
      price: price,
      price_type: 'custom',
      mrp: 0,
      hsn_code: '',
      tax_percent: taxPercent,
      tax_amount: taxCalc.taxAmount,
      item_discount: 0,
      total: taxCalc.total,
      max_stock: 99999,
      is_custom_item: true,
      loose_sales_type: null,
      loose_sales_allowed: false
    }]);
    setShowQuickAdd(false);
    setQuickAddForm({ name: '', description: '', price: '', qty: 1, tax_percent: 0 });
  };

  const updateQuantity = (productId, newQty, batchId = null) => {
    // Allow decimal quantities (for loose items like kg, ltr)
    const qty = parseFloat(newQty);
    // Allow 0 in input field, validation will happen at bill creation
    if (isNaN(qty) || qty < 0) {
      return;
    }
    
    setCart(cart.map(item => {
      // Match by product_id and optionally batch_id
      const isMatch = batchId 
        ? (item.product_id === productId && item.batch_id === batchId)
        : (item.product_id === productId && !item.batch_id);
      
      if (isMatch) {
        if (qty > item.max_stock) {
          alert('Not enough stock available!');
          return item;
        }
        const taxCalc = calculateTaxForItem(item.price, item.tax_percent, qty, item.item_discount);
        return { 
          ...item, 
          quantity: qty, 
          tax_amount: taxCalc.taxAmount,
          total: taxCalc.total
        };
      }
      return item;
    }));
  };

  const updateItemDiscount = (productId, discount) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const taxCalc = calculateTaxForItem(item.price, item.tax_percent, item.quantity, discount);
        return { 
          ...item, 
          item_discount: discount,
          tax_amount: taxCalc.taxAmount,
          total: taxCalc.total
        };
      }
      return item;
    }));
  };

  const updateItemDescription = (productId, batchId, description) => {
    setCart(cart.map(item => {
      if (item.product_id === productId && (item.batch_id || null) === (batchId || null)) {
        return { ...item, description };
      }
      return item;
    }));
  };

  // Check if price editing is allowed for current user
  const canEditPrice = () => {
    if (!billSettings?.allow_price_edit) return false;
    if (billSettings.price_edit_permission === 'owner_and_cashiers') return true;
    return userType === 'owner';
  };

  // Update item price
  const updateItemPrice = (productId, newPrice, batchId = null) => {
    // Parse leniently so the field can be cleared / typed freely (empty or partial input
    // is treated as 0 for calculations, while the raw text is preserved for display).
    const parsed = parseFloat(newPrice);
    const price = (isNaN(parsed) || parsed < 0) ? 0 : parsed;

    setCart(cart.map(item => {
      const isMatch = batchId 
        ? (item.product_id === productId && item.batch_id === batchId)
        : (item.product_id === productId && !item.batch_id);
      
      if (isMatch) {
        const taxCalc = calculateTaxForItem(price, item.tax_percent, item.quantity, item.item_discount);
        return { 
          ...item, 
          priceInput: newPrice,   // raw string so the input can be cleared / edited freely
          price: price,
          price_type: 'manual',
          tax_amount: taxCalc.taxAmount,
          total: taxCalc.total
        };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    if (cart.length > 0 && window.confirm('Are you sure you want to clear the cart?')) {
      setCart([]);
      setBillDiscount({ type: 'percent', value: 0 });
    }
  };

  const calculateTotals = useCallback(() => {
    const taxMethod = billSettings?.tax_method || 'inclusive';
    const subtotal = cart.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const itemDiscountTotal = cart.reduce((sum, item) => sum + item.item_discount, 0);
    
    let billDiscountAmount = 0;
    if (billDiscount.type === 'percent') {
      billDiscountAmount = ((subtotal - itemDiscountTotal) * billDiscount.value) / 100;
    } else {
      billDiscountAmount = billDiscount.value;
    }
    
    const totalDiscount = itemDiscountTotal + billDiscountAmount;
    
    // Calculate tax based on tax method
    const taxAmount = cart.reduce((sum, item) => {
      const itemBase = (item.quantity * item.price) - item.item_discount;
      const billDiscountRatio = billDiscount.type === 'percent' 
        ? billDiscount.value / 100 
        : billDiscountAmount / (subtotal - itemDiscountTotal || 1);
      const afterBillDiscount = itemBase * (1 - billDiscountRatio);
      
      if (taxMethod === 'inclusive') {
        // Tax is included - reverse calculate
        const taxDivisor = 1 + (item.tax_percent / 100);
        const preTaxAmount = afterBillDiscount / taxDivisor;
        return sum + (afterBillDiscount - preTaxAmount);
      } else {
        // Tax exclusive - add on top
        return sum + (afterBillDiscount * item.tax_percent / 100);
      }
    }, 0);
    
    // For inclusive: grandTotal = subtotal - totalDiscount (tax already in price)
    // For exclusive: grandTotal = subtotal - totalDiscount + taxAmount
    const taxableAmount = subtotal - totalDiscount;
    const grandTotalRaw = taxMethod === 'inclusive' ? taxableAmount : taxableAmount + taxAmount;

    // Apply grand-total round-off if enabled (Bill Summary toggle)
    const grandTotal = roundOffEnabled ? Math.round(grandTotalRaw) : grandTotalRaw;
    const roundOff = roundOffEnabled ? (grandTotal - grandTotalRaw) : 0;
    
    const totalSavings = cart.reduce((sum, item) => sum + (item.mrp - item.price) * item.quantity, 0) + totalDiscount;
    
    return { subtotal, itemDiscountTotal, billDiscountAmount, totalDiscount, taxAmount, grandTotal, roundOff, totalSavings, taxMethod };
  }, [cart, billDiscount, billSettings, roundOffEnabled]);

  const handleCreateBill = () => {
    if (cart.length === 0) {
      alert('Please add items to cart');
      return;
    }

    // Validate zero quantity items
    const zeroQtyItems = cart.filter(item => !item.quantity || item.quantity <= 0);
    if (zeroQtyItems.length > 0) {
      alert(`Please enter valid quantity for: ${zeroQtyItems.map(i => i.product_name).join(', ')}`);
      return;
    }

    // If estimation mode, skip payment and directly save
    if (isEstimation) {
      handleSaveEstimation();
      return;
    }

    const totals = calculateTotals();
    // Use default bill mode from settings, fallback to 'cash'
    const defaultMode = billSettings?.default_bill_mode || 'cash';
    setPaymentModes([{ mode: defaultMode, amount: totals.grandTotal }]);
    setShowPaymentModal(true);
  };

  // Direct save for estimations (no payment modal)
  const handleSaveEstimation = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const isDeliveryEstimation = billSettings?.estimation_with_delivery || false;
      const totals = calculateTotals();

      // For delivery estimation: zero out tax
      const items = cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        unit: (products.find(p => p.id === item.product_id) || {}).unit || item.unit || '',
        quantity: item.quantity,
        price: item.price,
        tax_percent: isDeliveryEstimation ? 0 : item.tax_percent,
        tax_amount: isDeliveryEstimation ? 0 : item.tax_amount,
        hsn_code: item.hsn_code || '',
        mrp: item.mrp || 0,
        total: isDeliveryEstimation ? (item.price * item.quantity) : item.total,
        stock_quantity: item.stock_quantity ?? null,
        stock_unit: item.stock_unit || null,
        serial_numbers: item.serial_numbers || [],
        serial_number_values: item.serial_number_values || [],
        is_custom_item: item.is_custom_item || false
      }));

      const subtotal = items.reduce((s, i) => s + (i.price * i.quantity), 0);
      const grandTotal = isDeliveryEstimation ? subtotal - (totals.totalDiscount || 0) : totals.grandTotal;

      const estimationData = {
        customer_id: selectedCustomer?.id || 'guest',
        customer_name: selectedCustomer?.name || 'Guest',
        customer_mobile: selectedCustomer?.mobile || '',
        customer_gst_number: selectedCustomer?.gst_number || '',
        customer_email: selectedCustomer?.email || '',
        customer_address: selectedCustomer?.address || '',
        items: items,
        subtotal: subtotal,
        discount_percent: billDiscount.type === 'percent' ? billDiscount.value : 0,
        discount_amount: totals.totalDiscount,
        tax_amount: isDeliveryEstimation ? 0 : totals.taxAmount,
        total: grandTotal,
        grand_total: grandTotal,
        payment_modes: [],
        change_amount: 0,
        notes: billNotes,
        is_estimation: true,
        is_delivery_estimation: isDeliveryEstimation,
        affect_stock: isDeliveryEstimation,
        status: 'estimation'
      };

      const response = await axios.post(`${API}/estimations`, estimationData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Prepare print data and show preview directly
      const createdEstimation = {
        ...response.data,
        ...estimationData,
        estimation_number: response.data.estimation_number,
        is_estimation: true,
        created_at: response.data.created_at || new Date().toISOString()
      };

      setPrintBillData(createdEstimation);
      setShowPrintPreview(true);

      // Reset form
      setCart([]);
      setBillDiscount({ type: 'percent', value: 0 });
      setBillNotes('');
      selectGuestCustomer();
    } catch (error) {
      console.error('Error creating estimation:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to create estimation'));
    } finally {
      setLoading(false);
    }
  };

  const handleHoldBill = async () => {
    if (cart.length === 0) {
      alert('Please add items to cart');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const totals = calculateTotals();

      const billData = {
        customer_id: selectedCustomer?.id || 'guest',
        customer_name: selectedCustomer?.name || 'Walk-in Customer',
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          unit: (products.find(p => p.id === item.product_id) || {}).unit || item.unit || '',
          quantity: item.quantity,
          price: item.price,
          tax_percent: item.tax_percent,
          tax_amount: item.tax_amount,
          hsn_code: item.hsn_code || '',
          mrp: item.mrp || 0,
          total: item.total,
          description: item.description || '',
          is_custom_item: item.is_custom_item || false,
          stock_quantity: item.stock_quantity ?? null,
          stock_unit: item.stock_unit || null,
          serial_numbers: item.serial_numbers || [],
          serial_number_values: item.serial_number_values || []
        })),
        subtotal: totals.subtotal,
        discount_percent: billDiscount.type === 'percent' ? billDiscount.value : 0,
        discount_amount: totals.totalDiscount,
        tax_amount: totals.taxAmount,
        grand_total: totals.grandTotal,
        round_off: totals.roundOff || 0,
        payment_modes: [],
        status: 'held'
      };

      await axios.post(`${API}/bills/hold`, billData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Bill held successfully!');
      setCart([]);
      setBillDiscount({ type: 'percent', value: 0 });
      selectGuestCustomer();
      fetchHeldBills();
    } catch (error) {
      console.error('Error holding bill:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to hold bill'));
    } finally {
      setLoading(false);
    }
  };

  const handleResumeBill = async (bill) => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/bills/${bill.id}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const resumedBill = response.data;
      
      // Restore cart from held bill
      const restoredCart = resumedBill.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        price: item.price,
        mrp: item.mrp || item.price, // Approximate
        hsn_code: item.hsn_code || '',
        unit: item.unit || null,
        description: item.description || '',
        tax_percent: item.tax_percent,
        tax_amount: item.tax_amount,
        item_discount: item.item_discount || 0,
        total: item.total,
        stock_quantity: item.stock_quantity ?? null,
        stock_unit: item.stock_unit || null,
        track_stock_separately: item.stock_quantity != null,
        max_stock: 999
      }));

      setCart(restoredCart);
      
      // Find customer
      const customer = customers.find(c => c.id === resumedBill.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
        fetchCustomerCustomPrices(customer.id);
      }

      // Delete the held bill
      await axios.delete(`${API}/bills/${bill.id}/delete-held`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Ignore if endpoint doesn't exist

      setShowHeldBillsModal(false);
      fetchHeldBills();
    } catch (error) {
      console.error('Error resuming bill:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to resume bill'));
    }
  };

  const handlePaymentSubmit = async () => {
    const totals = calculateTotals();
    const totalPaid = paymentModes.reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0);
    
    // Check if any payment mode is credit
    const hasCreditPayment = paymentModes.some(pm => pm.mode === 'credit' && pm.amount > 0);
    
    // Validate credit sales - must select a real customer (not Guest)
    if (hasCreditPayment && (!selectedCustomer || selectedCustomer.is_guest || selectedCustomer.id === 'guest')) {
      alert('Credit sales require a customer. Please select or add a customer before proceeding with credit payment.');
      return;
    }
    
    // Skip payment validation for estimations
    if (!isEstimation && totalPaid < totals.grandTotal) {
      alert('Payment amount is less than bill total!');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');

      // Calculate change amount
      const changeAmount = isEstimation ? 0 : Math.max(0, totalPaid - totals.grandTotal);
      
      // Adjust payment modes to record actual sale amounts, not cash received
      // When customer pays Rs.100 for Rs.80 bill, we should record Rs.80 in cash sales, not Rs.100
      let adjustedPaymentModes = [];
      if (!isEstimation) {
        let remainingChange = changeAmount;
        adjustedPaymentModes = paymentModes.filter(pm => pm.amount > 0).map(pm => {
          if (pm.mode === 'cash' && remainingChange > 0) {
            // Deduct change from cash payment to get actual cash sale amount
            const adjustedAmount = Math.max(0, pm.amount - remainingChange);
            remainingChange = Math.max(0, remainingChange - pm.amount);
            return { ...pm, amount: adjustedAmount };
          }
          return pm;
        }).filter(pm => pm.amount > 0); // Remove if amount becomes 0
      }
      
      const billData = {
        customer_id: selectedCustomer?.id || 'guest',
        customer_name: selectedCustomer?.name || 'Walk-in Customer',
        customer_mobile: selectedCustomer?.mobile || '',
        customer_gst_number: selectedCustomer?.gst_number || '',
        customer_email: selectedCustomer?.email || '',
        customer_address: selectedCustomer?.address || '',
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          unit: (products.find(p => p.id === item.product_id) || {}).unit || item.unit || '',
          quantity: item.quantity,
          price: item.price,
          tax_percent: item.tax_percent,
          tax_amount: item.tax_amount,
          hsn_code: item.hsn_code || '',
          mrp: item.mrp || 0,
          total: item.total,
          description: item.description || '',
          is_custom_item: item.is_custom_item || false,
          stock_quantity: item.stock_quantity ?? null,
          stock_unit: item.stock_unit || null,
          serial_numbers: item.serial_numbers || [],
          serial_number_values: item.serial_number_values || []
        })),
        subtotal: totals.subtotal,
        discount_percent: billDiscount.type === 'percent' ? billDiscount.value : 0,
        discount_amount: totals.totalDiscount,
        tax_amount: totals.taxAmount,
        total: totals.grandTotal,
        grand_total: totals.grandTotal,
        round_off: totals.roundOff || 0,
        payment_modes: adjustedPaymentModes,
        change_amount: changeAmount,
        notes: billNotes,
        is_estimation: isEstimation,
        status: isEstimation ? 'estimation' : 'completed',
        bill_date: selectedBillDate || null
      };

      const endpoint = isEstimation ? `${API}/estimations` : `${API}/bills`;
      let response;
      if (isEditMode && editBillId) {
        // EDIT mode: PUT to bills (estimations edit not supported on server yet — falls back to PUT bills only)
        response = await axios.put(`${API}/bills/${editBillId}`, billData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Server returns {message, bill: {...}}; unwrap
        if (response.data && response.data.bill) {
          response = { ...response, data: response.data.bill };
        }
      } else {
        response = await axios.post(endpoint, billData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Prepare print data
      const createdBill = {
        ...response.data,
        ...billData,
        bill_number: response.data.bill_number || response.data.estimation_number,
        estimation_number: response.data.estimation_number,
        created_at: selectedBillDate || response.data.created_at || new Date().toISOString()
      };

      setPrintBillData(createdBill);
      setShowPaymentModal(false);
      setShowPrintPreview(true);

      // Reset form
      setCart([]);
      setBillDiscount({ type: 'percent', value: 0 });
      // Use default bill mode from settings for next bill
      const defaultMode = billSettings?.default_bill_mode || 'cash';
      setPaymentModes([{ mode: defaultMode, amount: 0 }]);
      setBillNotes('');
      selectGuestCustomer();
      
      if (!isEstimation) {
        fetchProducts(); // Refresh stock only for actual bills
      }

      // In edit mode, after successful update, redirect back to bills history
      if (isEditMode && editBillId) {
        setTimeout(() => navigate('/gposp/bills-history'), 1500);
      }
    } catch (error) {
      console.error('Error creating bill:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to create ' + (isEstimation ? 'estimation' : 'bill')));
    } finally {
      setLoading(false);
    }
  };

  const addPaymentMode = () => {
    setPaymentModes([...paymentModes, { mode: 'upi', amount: 0 }]);
  };

  const removePaymentMode = (index) => {
    if (paymentModes.length > 1) {
      setPaymentModes(paymentModes.filter((_, i) => i !== index));
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.barcode && p.barcode.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile.includes(customerSearch)
  );

  const totals = calculateTotals();
  const totalPaid = paymentModes.reduce((sum, pm) => sum + parseFloat(pm.amount || 0), 0);
  const balanceChange = totalPaid - totals.grandTotal;

  return (
    <div data-testid="pos-billing-page" className="h-[calc(100vh-7rem)] flex flex-col lg:flex-row gap-4 relative">
      {/* Edit Mode Banner */}
      {isEditMode && (
        <div className="absolute top-0 left-0 right-0 z-40 bg-amber-100 border-b border-amber-300 px-4 py-2 flex items-center justify-between" data-testid="edit-mode-banner">
          <span className="text-sm font-medium text-amber-800">
            ✎ Editing Bill {editBillId?.slice(0, 8)}… — changes will rebalance stock & ledger on save
          </span>
          <button onClick={() => navigate('/gposp/bills-history')} className="text-xs text-amber-700 underline hover:text-amber-900" data-testid="edit-mode-exit">
            Cancel Edit
          </button>
        </div>
      )}
      {/* Cash Register Closed Overlay - Only show when NOT in estimation mode */}
      {!registerLoading && registerStatus?.status === 'closed' && !isEstimation && (
        <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-md flex items-center justify-center p-4" style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-2xl shadow-2xl text-center p-6 sm:p-8 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">Cash Register Closed</h2>
            <p className="text-sm sm:text-base text-slate-600 mb-6">Open the cash register to start billing, or create an estimation.</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/gposp/cash-register'}
                className="w-full px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all text-sm sm:text-base"
                data-testid="open-register-btn"
              >
                Open Cash Register
              </button>
              <button
                onClick={() => setIsEstimation(true)}
                className="w-full px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-all text-sm sm:text-base"
                data-testid="switch-to-estimation-btn"
              >
                Create Estimation Instead
              </button>
              <p className="text-xs text-slate-400 mt-2">Estimations don't affect stock</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Left Panel - Product Search & Cart */}
      <div className="flex-1 flex flex-col space-y-3 min-w-0">
        {/* Customer Selection */}
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                ref={customerSearchRef}
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search customer (F4)..."
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
                data-testid="customer-search"
              />
              {customerSearch && filteredCustomers.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                  {filteredCustomers.slice(0, 5).map(customer => (
                    <button
                      key={customer.id}
                      onClick={() => { 
                        setSelectedCustomer(customer); 
                        setCustomerSearch(''); 
                        fetchCustomerCustomPrices(customer.id);
                        // Auto-focus product search after customer selection
                        setTimeout(() => searchInputRef.current?.focus(), 100);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-violet-50 transition-colors"
                      data-testid={`customer-option-${customer.id}`}
                    >
                      <p className="font-medium text-slate-900 text-sm">{customer.name}</p>
                      <p className="text-xs text-slate-500">{customer.mobile}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowCustomerModal(true)}
              className="px-4 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium text-sm whitespace-nowrap"
              data-testid="add-customer-btn"
            >
              + New
            </button>
          </div>
          
          {selectedCustomer && (
            <div className="mt-2 p-2.5 bg-violet-50 border border-violet-200 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-semibold text-violet-900 text-sm">{selectedCustomer.name}</p>
                {selectedCustomer.mobile && !selectedCustomer.is_guest && (
                  <p className="text-xs text-violet-600">
                    {selectedCustomer.mobile}
                    {selectedCustomer.pricing_level > 1 && (
                      <span className="ml-2 px-1.5 py-0.5 bg-violet-200 text-violet-700 rounded text-xs font-medium">
                        Level {selectedCustomer.pricing_level}
                      </span>
                    )}
                  </p>
                )}
              </div>
              {!selectedCustomer.is_guest && (
                <button
                  onClick={selectGuestCustomer}
                  className="text-xs text-violet-600 hover:text-violet-800"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Product Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
            <input
              ref={searchInputRef}
              type="text"
              value={productSearch}
              onChange={(e) => { setProductSearch(e.target.value); setShowProductSearch(true); setHighlightIndex(0); }}
              onFocus={() => { setShowProductSearch(true); setHighlightIndex(0); }}
              onKeyDown={(e) => {
                const visible = filteredProducts.slice(0, 10).filter(p => p.current_stock > 0 || businessInfo?.inventory_model === 'no_inventory');
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightIndex(prev => Math.min(prev + 1, visible.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightIndex(prev => Math.max(prev - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  // Barcode scanner: if exact barcode match, add directly
                  const barcodeMatch = products.find(p => 
                    (p.barcode && p.barcode === productSearch.trim()) || (p.sku && p.sku === productSearch.trim())
                  );
                  if (barcodeMatch && (barcodeMatch.current_stock > 0 || businessInfo?.inventory_model === 'no_inventory')) {
                    addToCart(barcodeMatch);
                    setProductSearch('');
                    setShowProductSearch(false);
                    setHighlightIndex(-1);
                  } else if (highlightIndex >= 0 && highlightIndex < visible.length) {
                    addToCart(visible[highlightIndex]);
                    setProductSearch('');
                    setShowProductSearch(false);
                    setHighlightIndex(-1);
                  }
                } else if (e.key === 'Escape') {
                  setShowProductSearch(false);
                  setHighlightIndex(-1);
                }
              }}
              placeholder="Search product by name, SKU, or scan barcode... (F2)"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 pr-20"
              data-testid="product-search"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">F2</span>
            </div>
          
            {/* Quick Add Custom Item Button */}
            {billSettings?.allow_quick_add_items && (
              (billSettings.quick_add_permission === 'owner_and_cashiers' || userType === 'owner') && (
                <button
                  onClick={() => setShowQuickAdd(true)}
                  className="h-[46px] px-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-medium whitespace-nowrap flex items-center justify-center"
                  data-testid="quick-add-btn"
                  title="Add custom item"
                >
                  + Custom
                </button>
              )
            )}
            
            {/* Camera Barcode Scanner Button */}
            <button
              onClick={() => setShowScanner(true)}
              className="h-[46px] px-3 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center"
              data-testid="barcode-scan-btn"
              title="Scan barcode with camera"
            >
              <Camera size={20} />
            </button>

            {/* Primary Products Drawer Toggle (cafe-style) */}
            {billSettings?.show_primary_drawer && (
              <button
                onClick={() => setShowPrimaryDrawer(true)}
                className="h-[46px] px-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center"
                data-testid="open-primary-drawer-btn"
                title="Open Quick Pick drawer"
              >
                <Grid3x3 size={20} />
              </button>
            )}
          </div>
          
          {showProductSearch && productSearch && (
            <div className="mt-2 max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-testid="product-search-results">
              {filteredProducts.length === 0 ? (
                <p className="px-4 py-3 text-slate-500 text-sm">No products found</p>
              ) : (
                filteredProducts.slice(0, 10).map((product, idx) => {
                  const isHighlighted = idx === highlightIndex;
                  return (
                    <button
                      key={product.id}
                      onClick={() => { addToCart(product); setProductSearch(''); setShowProductSearch(false); setHighlightIndex(-1); }}
                      disabled={product.current_stock <= 0 && businessInfo?.inventory_model !== 'no_inventory'}
                      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${
                        product.current_stock <= 0 && businessInfo?.inventory_model !== 'no_inventory' ? 'opacity-50 cursor-not-allowed bg-slate-50' :
                        isHighlighted ? 'bg-violet-100 border-l-4 border-l-violet-500' : 'hover:bg-emerald-50'
                      }`}
                      data-testid={`product-option-${product.id}`}
                    >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{product.name}</p>
                        <p className="text-xs text-slate-500">
                          SKU: {product.sku} | Stock: {product.current_stock}
                          {product.current_stock <= product.stock_alert_quantity && product.current_stock > 0 && (
                            <span className="text-slate-600 ml-1">(Low)</span>
                          )}
                        </p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="font-semibold text-violet-600">₹{getProductPrice(product).price.toFixed(2)}</p>
                        {product.mrp > getProductPrice(product).price && (
                          <p className="text-xs text-slate-400 line-through">₹{product.mrp.toFixed(2)}</p>
                        )}
                        {getProductPrice(product).type === 'custom' ? (
                          <p className="text-xs text-emerald-600 font-medium">(Custom)</p>
                        ) : selectedCustomer?.pricing_level > 1 && getProductPrice(product).type !== 'default' ? (
                          <p className="text-xs text-emerald-600">L{selectedCustomer.pricing_level} Price</p>
                        ) : null}
                      </div>
                    </div>
                  </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Cart */}
        <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col min-h-0">
          <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-sm">Cart ({cart.length} items)</h3>
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                className="text-xs text-red-600 hover:text-red-700"
                data-testid="clear-cart-btn"
              >
                Clear All
              </button>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {cart.map((item, index) => (
              <div key={`${item.product_id}-${item.batch_id || 'no-batch'}`} className="p-3 bg-slate-50 rounded-lg" data-testid={`cart-item-${index}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{item.product_name}</p>
                    {item.batch_number && (
                      <p className="text-xs text-blue-600 font-medium">
                        Batch: {item.batch_number}
                        {item.expiry_date && (
                          <span className="text-slate-400 ml-1">
                            (Exp: {new Date(item.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })})
                          </span>
                        )}
                      </p>
                    )}
                    {item.serial_number_values?.length > 0 && (
                      <p className="text-xs text-orange-600 font-medium truncate" title={item.serial_number_values.join(', ')}>
                        SN: {item.serial_number_values.join(', ')}
                      </p>
                    )}
                    {item.stock_quantity != null && (
                      <p className="text-xs text-amber-600 font-medium">
                        Stock deduct: {item.stock_quantity} {item.stock_unit || 'pcs'}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      ₹{item.price.toFixed(2)} × {item.quantity}{item.stock_quantity != null && item.unit ? ` ${item.unit}` : ''}
                      {item.price_type === 'custom' && (
                        <span className="ml-1 text-emerald-600 font-medium">(Custom)</span>
                      )}
                      {item.price_type && item.price_type.startsWith('level_') && (
                        <span className="ml-1 text-violet-600">(L{item.price_type.split('_')[1]})</span>
                      )}
                      {item.price_type && item.price_type.startsWith('batch_level_') && (
                        <span className="ml-1 text-blue-600">(Batch L{item.price_type.split('_')[2]})</span>
                      )}
                    </p>
                    {item.tax_percent > 0 && (
                      <p className="text-xs text-slate-400">Tax: {item.tax_percent}%</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        // Use 1 for regular products, fractional (0.5) for loose products
                        const isLoose = item.loose_sales_type || item.loose_sales_allowed;
                        const decrement = isLoose ? 0.5 : 1;
                        updateQuantity(item.product_id, Math.max(0, item.quantity - decrement), item.batch_id);
                      }}
                      className="w-7 h-7 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors font-bold text-sm"
                      data-testid={`qty-minus-${index}`}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      step={(item.loose_sales_type || item.loose_sales_allowed) ? "0.1" : "1"}
                      min="0"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.product_id, parseFloat(e.target.value) || 0, item.batch_id)}
                      className={`w-16 text-center border rounded py-1 text-sm ${item.quantity <= 0 ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                      data-testid={`qty-input-${index}`}
                    />
                    <button
                      onClick={() => {
                        // Use 1 for regular products, fractional (0.5) for loose products
                        const isLoose = item.loose_sales_type || item.loose_sales_allowed;
                        const increment = isLoose ? 0.5 : 1;
                        updateQuantity(item.product_id, item.quantity + increment, item.batch_id);
                      }}
                      className="w-7 h-7 bg-emerald-100 text-emerald-600 rounded hover:bg-emerald-200 transition-colors font-bold text-sm"
                      data-testid={`qty-plus-${index}`}
                    >
                      +
                    </button>
                  </div>
                  
                  <div className="text-right w-20">
                    <p className="font-semibold text-slate-900 text-sm">₹{item.total.toFixed(2)}</p>
                  </div>
                  
                  <button
                    onClick={() => removeFromCart(item.product_id)}
                    className="w-7 h-7 text-red-500 hover:bg-red-100 rounded transition-colors text-lg"
                    data-testid={`remove-item-${index}`}
                  >
                    ×
                  </button>
                </div>
                
                {/* Price Edit & Item Discount */}
                <div className="mt-2 flex items-center gap-4">
                  {canEditPrice() && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Price:</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.priceInput !== undefined ? item.priceInput : item.price}
                        onChange={(e) => updateItemPrice(item.product_id, e.target.value, item.batch_id)}
                        onFocus={(e) => e.target.select()}
                        className="w-20 px-2 py-1 border border-violet-200 rounded text-xs bg-violet-50"
                        data-testid={`item-price-${index}`}
                      />
                      {item.price_type === 'manual' && (
                        <span className="text-xs text-violet-600">(Edited)</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Discount:</span>
                    <input
                      type="number"
                      value={item.item_discount}
                      onChange={(e) => updateItemDiscount(item.product_id, parseFloat(e.target.value) || 0)}
                      className="w-20 px-2 py-1 border border-slate-200 rounded text-xs"
                      placeholder="₹0"
                      data-testid={`item-discount-${index}`}
                    />
                  </div>
                </div>

                {/* Inline Description (per cart line, optional) */}
                <div className="mt-2 flex items-start gap-2">
                  <span className="text-xs text-slate-500 pt-1">Note:</span>
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => updateItemDescription(item.product_id, item.batch_id, e.target.value.slice(0, 120))}
                    maxLength={120}
                    placeholder="Optional description shown on bill (e.g. Color: Red, Size: XL)"
                    className="flex-1 px-2 py-1 border border-slate-200 rounded text-xs italic placeholder:not-italic"
                    data-testid={`item-description-${index}`}
                  />
                </div>
              </div>
            ))}
            
            {cart.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <svg className="w-16 h-16 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-sm">Cart is empty</p>
                <p className="text-xs mt-1">Search and add products to start billing</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - Bill Summary */}
      <div className="lg:w-80 xl:w-96 flex flex-col space-y-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">Bill Summary</h3>
          
          {/* Previous Date Billing */}
          {billSettings?.allow_previous_date_bill && (
            billSettings.previous_date_permission === 'owner_and_cashiers' || userType === 'owner'
          ) && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              <input
                type="date"
                value={selectedBillDate}
                onChange={(e) => setSelectedBillDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="flex-1 px-2 py-1 text-sm border border-amber-300 rounded bg-white focus:ring-2 focus:ring-amber-400"
                data-testid="bill-date-picker"
              />
              {selectedBillDate && (
                <button onClick={() => setSelectedBillDate('')} className="text-amber-600 hover:text-amber-800 text-xs font-medium">Today</button>
              )}
            </div>
          )}
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Subtotal:</span>
              <span className="font-medium">₹{totals.subtotal.toFixed(2)}</span>
            </div>
            
            {totals.itemDiscountTotal > 0 && (
              <div className="flex justify-between text-orange-600">
                <span>Item Discounts:</span>
                <span>-₹{totals.itemDiscountTotal.toFixed(2)}</span>
              </div>
            )}
            
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Bill Discount:</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={billDiscount.value}
                  onChange={(e) => setBillDiscount({ ...billDiscount, value: parseFloat(e.target.value) || 0 })}
                  className="w-14 px-2 py-1 border border-slate-200 rounded text-center text-xs"
                  min="0"
                  data-testid="bill-discount-input"
                />
                <select
                  value={billDiscount.type}
                  onChange={(e) => setBillDiscount({ ...billDiscount, type: e.target.value })}
                  className="px-2 py-1 border border-slate-200 rounded text-xs"
                  data-testid="bill-discount-type"
                >
                  <option value="percent">%</option>
                  <option value="fixed">₹</option>
                </select>
              </div>
            </div>

            {totals.billDiscountAmount > 0 && (
              <div className="flex justify-between text-orange-600">
                <span>Discount Amount:</span>
                <span>-₹{totals.billDiscountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-slate-600">
                Tax (GST){totals.taxMethod === 'inclusive' ? ' (Incl.)' : ''}:
              </span>
              <span className="font-medium">₹{totals.taxAmount.toFixed(2)}</span>
            </div>

            {totals.totalSavings > 0 && (
              <div className="flex justify-between text-green-600 bg-green-50 -mx-4 px-4 py-2">
                <span>You Save:</span>
                <span className="font-medium">₹{totals.totalSavings.toFixed(2)}</span>
              </div>
            )}

            {roundOffEnabled && Math.abs(totals.roundOff) >= 0.005 && (
              <div className="flex justify-between text-slate-600">
                <span>Round Off:</span>
                <span className="font-medium" data-testid="round-off-amount">{totals.roundOff >= 0 ? '+' : ''}₹{totals.roundOff.toFixed(2)}</span>
              </div>
            )}

            <div className="pt-3 border-t border-slate-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Grand Total:</span>
                <span className="text-2xl font-bold text-violet-600" data-testid="grand-total">
                  ₹{totals.grandTotal.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Round Off Toggle */}
            <div className="flex items-center justify-between bg-slate-50 -mx-4 px-4 py-3 mt-2">
              <div>
                <span className="font-medium text-slate-700">Round Off Total</span>
                <p className="text-xs text-slate-500">Round grand total to nearest ₹</p>
              </div>
              <button
                onClick={async () => {
                  const next = !roundOffEnabled;
                  setRoundOffEnabled(next);
                  try {
                    const token = localStorage.getItem('pos_token');
                    await axios.put(`${API}/settings/bill`, { enable_round_off: next }, { headers: { Authorization: `Bearer ${token}` } });
                    setBillSettings(prev => ({ ...(prev || {}), enable_round_off: next }));
                  } catch (e) { /* non-blocking persistence */ }
                }}
                className={`w-12 h-6 rounded-full transition-colors ${roundOffEnabled ? 'bg-violet-600' : 'bg-slate-300'}`}
                data-testid="round-off-toggle"
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${roundOffEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Estimation Toggle */}
            <div className="flex items-center justify-between bg-slate-50 -mx-4 px-4 py-3 mt-2">
              <div>
                <span className="font-medium text-slate-700">Estimation Mode</span>
                <p className="text-xs text-slate-500">Stock won't be affected</p>
              </div>
              <button
                onClick={() => setIsEstimation(!isEstimation)}
                className={`w-12 h-6 rounded-full transition-colors ${isEstimation ? 'bg-violet-600' : 'bg-slate-300'}`}
                data-testid="estimation-toggle"
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${isEstimation ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          <div className="space-y-2 pt-2">
            <button
              onClick={handleCreateBill}
              disabled={cart.length === 0 || (!isEstimation && registerStatus?.status === 'closed')}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="pay-btn"
            >
              <span>{isEstimation ? 'Create Estimation' : 'Pay Now'}</span>
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded">F3</span>
            </button>
            
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleHoldBill}
                disabled={cart.length === 0 || loading || registerStatus?.status === 'closed'}
                className="px-4 py-2.5 border-2 border-slate-400 text-slate-700 rounded-xl font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
                data-testid="hold-btn"
              >
                Hold Bill
              </button>
              <button
                onClick={() => setShowHeldBillsModal(true)}
                className="px-4 py-2.5 border-2 border-blue-500 text-blue-600 rounded-xl font-medium hover:bg-blue-50 transition-colors relative"
                data-testid="resume-btn"
              >
                Resume
                {heldBills.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                    {heldBills.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* New Customer Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add New Customer</h3>
              <button onClick={() => setShowCustomerModal(false)} className="text-2xl text-slate-400 hover:text-slate-600">×</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="new-customer-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile *</label>
                <input
                  type="tel"
                  value={newCustomer.mobile}
                  onChange={(e) => setNewCustomer({ ...newCustomer, mobile: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  maxLength="10"
                  data-testid="new-customer-mobile"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="new-customer-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
                <input
                  type="text"
                  value={newCustomer.gst_number}
                  onChange={(e) => setNewCustomer({ ...newCustomer, gst_number: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="For B2B customers"
                  data-testid="new-customer-gst"
                />
              </div>
              <button
                onClick={addNewCustomer}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-2.5 rounded-lg font-medium hover:shadow-lg transition-all"
                data-testid="save-customer-btn"
              >
                Add Customer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Payment</h3>
                  <p className="text-2xl font-bold text-violet-600 mt-1">₹{totals.grandTotal.toFixed(2)}</p>
                </div>
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">Enter on amount to pay</span>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Customer Advance Balance Hint */}
              {selectedCustomer && selectedCustomer.id !== 'guest' && (selectedCustomer.advance_balance > 0 || selectedCustomer.outstanding_balance > 0) && (
                <div className={`p-3 rounded-lg text-sm border ${selectedCustomer.advance_balance > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="customer-balance-hint">
                  {selectedCustomer.advance_balance > 0 ? (
                    <span><strong>Advance Balance:</strong> ₹{selectedCustomer.advance_balance.toFixed(2)} available — will auto-apply on credit portion.</span>
                  ) : (
                    <span><strong>Outstanding Balance:</strong> ₹{selectedCustomer.outstanding_balance.toFixed(2)} pending.</span>
                  )}
                </div>
              )}
              {paymentModes.map((pm, index) => (
                <div key={`payment-${index}`} className="flex gap-2 items-center">
                  <select
                    value={pm.mode}
                    onChange={(e) => {
                      const updated = [...paymentModes];
                      updated[index].mode = e.target.value;
                      setPaymentModes(updated);
                    }}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid={`payment-mode-${index}`}
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                    <option value="netbanking">Net Banking</option>
                    <option value="credit">Credit</option>
                  </select>
                  <input
                    type="number"
                    value={pm.amount}
                    autoFocus={index === 0}
                    onChange={(e) => {
                      const updated = [...paymentModes];
                      updated[index].amount = parseFloat(e.target.value) || 0;
                      setPaymentModes(updated);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const currentTotal = paymentModes.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
                        if (!loading && currentTotal >= totals.grandTotal) {
                          handlePaymentSubmit();
                        }
                      }
                    }}
                    placeholder="Amount"
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg"
                    data-testid={`payment-amount-${index}`}
                  />
                  {paymentModes.length > 1 && (
                    <button
                      onClick={() => removePaymentMode(index)}
                      className="w-8 h-8 text-red-500 hover:bg-red-50 rounded"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              
              <button
                onClick={addPaymentMode}
                className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                data-testid="add-payment-mode-btn"
              >
                + Add Split Payment
              </button>
              
              <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Total Paid:</span>
                  <span className="font-medium">₹{totalPaid.toFixed(2)}</span>
                </div>
                {balanceChange >= 0 ? (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Change to Return:</span>
                    <span className="font-bold text-lg">₹{balanceChange.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Balance Due:</span>
                    <span className="font-bold">₹{Math.abs(balanceChange).toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="cancel-payment-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePaymentSubmit}
                  disabled={loading || totalPaid < totals.grandTotal}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white py-2.5 rounded-lg font-medium disabled:opacity-50 hover:shadow-lg transition-all"
                  data-testid="complete-payment-btn"
                >
                  {loading ? 'Processing...' : 'Complete Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Held Bills Modal */}
      {showHeldBillsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Held Bills ({heldBills.length})</h3>
              <button onClick={() => setShowHeldBillsModal(false)} className="text-2xl text-slate-400 hover:text-slate-600">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {heldBills.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No held bills</p>
              ) : (
                <div className="space-y-3">
                  {heldBills.map(bill => (
                    <div key={bill.id} className="p-4 bg-slate-50 rounded-lg" data-testid={`held-bill-${bill.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-800">{bill.customer_name}</p>
                          <p className="text-xs text-slate-500">{bill.bill_number}</p>
                          <p className="text-xs text-slate-400">{bill.items?.length || 0} items</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-violet-600">₹{bill.grand_total?.toFixed(2)}</p>
                          <button
                            onClick={() => handleResumeBill(bill)}
                            className="mt-2 px-4 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                            data-testid={`resume-bill-${bill.id}`}
                          >
                            Resume
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print Preview Modal */}
      {showPrintPreview && printBillData && (
        <ThermalBillPrint
          bill={printBillData}
          businessInfo={businessInfo}
          billSettings={billSettings}
          autoPrint={billSettings?.direct_print_on_payment || false}
          onClose={() => {
            setShowPrintPreview(false);
            setPrintBillData(null);
          }}
        />
      )}

      {/* Batch Picker Modal */}
      {showBatchPicker && batchPickerProduct && (
        <BatchPickerModal
          product={batchPickerProduct}
          batches={productBatches}
          customerLevel={selectedCustomer?.pricing_level || 1}
          onSelect={handleBatchSelect}
          onClose={() => {
            setShowBatchPicker(false);
            setBatchPickerProduct(null);
          }}
        />
      )}

      {/* Serial Number Picker Modal */}
      {showSerialPicker && serialPickerProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="serial-picker-modal">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-bold text-slate-800">Pick Serial Numbers</h3>
                <p className="text-xs text-slate-500">{serialPickerProduct.name}</p>
              </div>
              <button onClick={() => { setShowSerialPicker(false); setSerialPickerProduct(null); }} className="p-2 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
            </div>
            
            <div className="p-3 border-b border-slate-100">
              <input
                type="text"
                value={serialSearchText}
                onChange={e => setSerialSearchText(e.target.value)}
                placeholder="Search serial number..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                autoFocus
                data-testid="serial-picker-search"
              />
              <p className="text-xs text-slate-500 mt-1">Selected: {selectedSerials.length} | Available: {availableSerials.length}</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {availableSerials
                .filter(s => !serialSearchText || s.serial_number.toLowerCase().includes(serialSearchText.toLowerCase()))
                .map(sn => {
                  const isSelected = selectedSerials.some(s => s.id === sn.id);
                  return (
                    <button
                      key={sn.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedSerials(prev => prev.filter(s => s.id !== sn.id));
                        } else {
                          setSelectedSerials(prev => [...prev, sn]);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isSelected ? 'bg-orange-100 border-2 border-orange-400 text-orange-800' : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                      }`}
                      data-testid={`serial-option-${sn.serial_number}`}
                    >
                      <span className="font-mono font-medium">{sn.serial_number}</span>
                      {isSelected && <Check size={16} className="text-orange-600" />}
                    </button>
                  );
                })}
              {availableSerials.length === 0 && (
                <p className="text-center py-6 text-slate-400 text-sm">No available serial numbers. Add them in Stock Management.</p>
              )}
            </div>
            
            <div className="p-3 border-t border-slate-200">
              <button
                onClick={() => {
                  if (selectedSerials.length === 0) {
                    alert('Please select at least one serial number');
                    return;
                  }
                  addToCartWithSerials(serialPickerProduct, selectedSerials);
                }}
                disabled={selectedSerials.length === 0}
                className="w-full py-2.5 bg-orange-600 text-white rounded-lg font-medium text-sm hover:bg-orange-700 disabled:opacity-40"
                data-testid="serial-picker-confirm"
              >
                Add {selectedSerials.length} item(s) to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-slate-800 text-white">
              <h3 className="font-semibold text-sm">Scan Barcode</h3>
              <button onClick={() => {
                setShowScanner(false);
                if (scannerRef.current) {
                  try { scannerRef.current.stop(); } catch(e) {}
                  scannerRef.current = null;
                }
              }} className="text-xl">&times;</button>
            </div>
            <div id="barcode-scanner-region" style={{ width: '100%', minHeight: '280px' }}></div>
            <p className="text-center text-xs text-slate-500 py-2">Point camera at barcode</p>
          </div>
        </div>
      )}

      {/* Qty Entry Popup (Bill Settings → enable_qty_popup) */}
      {showQtyPopup && qtyPopupProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowQtyPopup(false)} data-testid="qty-popup-overlay">
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-800">{qtyPopupProduct.name}</h3>
              <button onClick={() => setShowQtyPopup(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">MRP: ₹{qtyPopupProduct.mrp || 0} · Stock: {qtyPopupProduct.current_stock}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Quantity{qtyPopupProduct.unit ? ` (${qtyPopupProduct.unit})` : ''} *
                </label>
                <input
                  type="number"
                  step="0.001"
                  autoFocus
                  value={qtyPopupForm.qty}
                  onChange={e => setQtyPopupForm({ ...qtyPopupForm, qty: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') confirmQtyPopup(); }}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500"
                  data-testid="qty-popup-qty"
                />
              </div>
              {billSettings?.enable_dual_unit_stock && qtyPopupProduct.track_stock_separately && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Pieces ({qtyPopupProduct.stock_unit || 'pcs'}) — deducted from stock *
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={qtyPopupForm.pcs}
                    onChange={e => setQtyPopupForm({ ...qtyPopupForm, pcs: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') confirmQtyPopup(); }}
                    className="w-full px-4 py-2.5 border border-amber-300 bg-amber-50 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="qty-popup-pcs"
                  />
                  <p className="text-[11px] text-amber-700 mt-1">Stock: {qtyPopupProduct.current_stock} {qtyPopupProduct.stock_unit || 'pcs'}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Price (per unit)</label>
                <input
                  type="number"
                  step="0.01"
                  value={qtyPopupForm.price}
                  onChange={e => setQtyPopupForm({ ...qtyPopupForm, price: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') confirmQtyPopup(); }}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500"
                  data-testid="qty-popup-price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Discount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={qtyPopupForm.discount}
                  onChange={e => setQtyPopupForm({ ...qtyPopupForm, discount: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') confirmQtyPopup(); }}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500"
                  data-testid="qty-popup-discount"
                />
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setShowQtyPopup(false)} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid="qty-popup-cancel">
                Cancel
              </button>
              <button onClick={confirmQtyPopup} className="flex-1 px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium" data-testid="qty-popup-confirm">
                Add to Cart
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">Press <kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">Enter</kbd> to add</p>
          </div>
        </div>
      )}

      {/* Primary Products Drawer (Cafe-style Quick Pick) */}
      {showPrimaryDrawer && (
        <div
          className="fixed inset-0 z-50 flex"
          onClick={() => setShowPrimaryDrawer(false)}
          data-testid="primary-drawer-overlay"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

          {/* Drawer panel: slides from left on desktop, from top on mobile */}
          <div
            className="relative bg-white shadow-2xl overflow-hidden flex flex-col
              w-full max-w-md md:max-w-md md:h-full md:w-96
              max-md:h-[80vh] max-md:w-full max-md:rounded-b-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="primary-drawer-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-violet-50">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Quick Pick</h3>
                <p className="text-xs text-slate-500">Tap an item to add to cart</p>
              </div>
              <button
                onClick={() => setShowPrimaryDrawer(false)}
                className="w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center"
                data-testid="close-primary-drawer-btn"
              >
                <X size={20} />
              </button>
            </div>

            {/* Category chips */}
            <div className="flex gap-2 p-3 overflow-x-auto border-b border-slate-100">
              <button
                onClick={() => setPrimaryCategoryFilter('')}
                className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${primaryCategoryFilter === '' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="primary-chip-all"
              >
                All
              </button>
              {[...new Set(primaryProducts.map(p => p.category_name).filter(Boolean))].map(cat => (
                <button
                  key={cat}
                  onClick={() => setPrimaryCategoryFilter(cat)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${primaryCategoryFilter === cat ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  data-testid={`primary-chip-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Product grid */}
            <div className="flex-1 overflow-y-auto p-3">
              {primaryProducts.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">
                  No primary products yet.<br/>
                  Mark products as <b>Primary</b> from the Products page to see them here.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {primaryProducts
                    .filter(p => !primaryCategoryFilter || p.category_name === primaryCategoryFilter)
                    .map(p => {
                      const outOfStock = (p.current_stock <= 0) && businessInfo?.inventory_model !== 'no_inventory';
                      return (
                        <button
                          key={p.id}
                          onClick={() => { if (!outOfStock) addToCart(p); }}
                          disabled={outOfStock}
                          className={`flex flex-col items-stretch text-left rounded-xl border transition-all overflow-hidden ${
                            outOfStock ? 'opacity-50 cursor-not-allowed border-slate-200' : 'border-slate-200 hover:border-emerald-400 hover:shadow-md active:scale-95'
                          }`}
                          data-testid={`primary-card-${p.id}`}
                        >
                          {/* Image (premium only) */}
                          {billSettings?.show_product_image_in_drawer && billingFeatures.product_images_in_billing && p.image ? (
                            <img src={p.image} alt={p.name} className="w-full h-24 object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-24 bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center">
                              <Package size={28} className="text-slate-300" />
                            </div>
                          )}
                          <div className="p-2">
                            <p className="text-sm font-semibold text-slate-800 line-clamp-2 min-h-[2.5rem]">{p.name}</p>
                            <div className="mt-1 flex items-center justify-between">
                              <span className="text-base font-bold text-emerald-700">₹{(p.selling_price || 0).toFixed(2)}</span>
                              {p.tax_percent > 0 && (
                                <span className="text-[10px] text-slate-500">+{p.tax_percent}% GST</span>
                              )}
                            </div>
                            {outOfStock && <p className="text-[10px] text-red-500 mt-1">Out of stock</p>}
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Custom Item Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Add Custom Item</h3>
              <button onClick={() => setShowQuickAdd(false)} className="text-2xl text-slate-400 hover:text-slate-600">&times;</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Item Name *</label>
                <input type="text" value={quickAddForm.name} onChange={e => setQuickAddForm({...quickAddForm, name: e.target.value})}
                  placeholder="Enter item name" className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500"
                  autoFocus data-testid="quick-add-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                <input type="text" value={quickAddForm.description}
                  onChange={e => setQuickAddForm({...quickAddForm, description: e.target.value.slice(0, 120)})}
                  maxLength={120}
                  placeholder="e.g. Service charge, Custom packaging"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 text-sm"
                  data-testid="quick-add-description" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price *</label>
                  <input type="number" value={quickAddForm.price} onChange={e => setQuickAddForm({...quickAddForm, price: e.target.value})}
                    placeholder="0.00" min="0" step="0.01" className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="quick-add-price" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Qty</label>
                  <input type="number" value={quickAddForm.qty} onChange={e => setQuickAddForm({...quickAddForm, qty: e.target.value})}
                    min="0.01" step="0.01" className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="quick-add-qty" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tax %</label>
                  <input type="number" value={quickAddForm.tax_percent} onChange={e => setQuickAddForm({...quickAddForm, tax_percent: e.target.value})}
                    min="0" max="28" step="0.5" className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="quick-add-tax" />
                </div>
              </div>
              <button onClick={addCustomItemToCart} disabled={!quickAddForm.name || !quickAddForm.price}
                className="w-full py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50"
                data-testid="quick-add-submit">
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSBilling;
